import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CommandPalette } from '@/components/shared/CommandPalette';
import { ShortcutsHelpModal } from '@/components/shared/ShortcutsHelpModal';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

interface KeyboardShortcutsContextValue {
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;
  isCommandPaletteOpen: boolean;
  openShortcutsHelp: () => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | null>(null);

export function useKeyboardShortcutsContext() {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcutsContext must be used within KeyboardShortcutsProvider');
  }
  return context;
}

interface KeyboardShortcutsProviderProps {
  children: ReactNode;
}

export function KeyboardShortcutsProvider({ children }: KeyboardShortcutsProviderProps) {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const { user } = useAuthStore();
  const { openModal } = useUIStore();
  const location = useLocation();

  // Only enable shortcuts for authenticated users and not on auth pages
  const isAuthPage = ['/login', '/signup', '/forgot-password', '/reset-password', '/confirm-email'].includes(location.pathname);
  const enabled = !!user && !isAuthPage;

  const openCommandPalette = useCallback(() => setCommandPaletteOpen(true), []);
  const closeCommandPalette = useCallback(() => setCommandPaletteOpen(false), []);
  const toggleCommandPalette = useCallback(() => setCommandPaletteOpen((prev) => !prev), []);
  const openShortcutsHelp = useCallback(() => setShortcutsHelpOpen(true), []);

  const handleNewProduct = useCallback(() => {
    openModal('new-product');
  }, [openModal]);

  const handleVoiceInput = useCallback(() => {
    // Trigger voice button click
    const voiceButton = document.querySelector('[data-voice-button]') as HTMLButtonElement;
    if (voiceButton) {
      voiceButton.click();
    }
  }, []);

  const handleExport = useCallback(() => {
    openModal('export');
  }, [openModal]);

  const { shortcutsByCategory } = useKeyboardShortcuts({
    enabled,
    onCommandPaletteToggle: toggleCommandPalette,
    onNewProduct: handleNewProduct,
    onVoiceInput: handleVoiceInput,
    onExport: handleExport,
    onShortcutsHelp: openShortcutsHelp,
  });

  const contextValue: KeyboardShortcutsContextValue = {
    openCommandPalette,
    closeCommandPalette,
    toggleCommandPalette,
    isCommandPaletteOpen: commandPaletteOpen,
    openShortcutsHelp,
  };

  return (
    <KeyboardShortcutsContext.Provider value={contextValue}>
      {children}

      {/* Command Palette - only render when authenticated */}
      {enabled && (
        <CommandPalette
          open={commandPaletteOpen}
          onOpenChange={setCommandPaletteOpen}
          onNewProduct={handleNewProduct}
          onVoiceInput={handleVoiceInput}
          onExport={handleExport}
        />
      )}

      {/* Shortcuts Help Modal */}
      {enabled && (
        <ShortcutsHelpModal
          open={shortcutsHelpOpen}
          onClose={() => setShortcutsHelpOpen(false)}
          shortcuts={shortcutsByCategory}
        />
      )}
    </KeyboardShortcutsContext.Provider>
  );
}

export default KeyboardShortcutsProvider;
